import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingSession, setProcessingSession] = useState(false);

  // Process session_id from URL fragment
  useEffect(() => {
    const processSessionId = async () => {
      const hash = window.location.hash;
      const match = hash.match(/#session_id=([^&]+)/);
      
      if (match) {
        const sessionId = match[1];
        console.log('📝 Processing session_id from URL...');
        setProcessingSession(true);
        setLoading(true);
        
        try {
          // Exchange session_id for session_token
          console.log('🔄 Exchanging session_id with backend...');
          const response = await axios.post(`${API}/auth/session?session_id=${sessionId}`, {}, {
            withCredentials: true
          });
          
          console.log('✅ Session exchange response:', response.data);
          
          if (response.data.success) {
            setUser(response.data.user);
            console.log('✅ User authenticated:', response.data.user.email);
            
            // Clean URL fragment
            window.history.replaceState(null, '', window.location.pathname);
            
            // Mark loading as complete
            setLoading(false);
            setProcessingSession(false);
          }
        } catch (error) {
          console.error('❌ Session exchange failed:', error);
          console.error('Error details:', error.response?.data);
          setLoading(false);
          setProcessingSession(false);
        }
      }
    };

    processSessionId();
  }, []);

  // Check existing session
  useEffect(() => {
    const checkAuth = async () => {
      // Skip if we're processing a session_id
      if (processingSession || window.location.hash.includes('session_id')) {
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true
        });
        setUser(response.data);
      } catch (error) {
        // Not authenticated
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [processingSession]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true
      });
      setUser(null);
      window.location.href = '/';
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const value = {
    user,
    loading: loading || processingSession,
    isAuthenticated: !!user,
    logout,
    processingSession
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
